import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Timestamp } from 'firebase/firestore';
import SignatureCanvas from 'react-signature-canvas';
import {
  getContainerVanChecklistTemplate,
  saveContainerVanChecklist,
  calculateContainerVanChecklistStats,
  generateContainerVanPDFSummary,
  convertColorStatusToDescriptive,
  uploadCategoryPhotos,
  type ContainerVanPDFGenerationParams
} from '../utils/containerVanChecklistUtils';
import type {
  ContainerVan,
  ContainerVanChecklistTemplate,
  ContainerVanChecklistData,
  ContainerVanChecklistCategory,
  ContainerVanChecklistItem
} from '../types/containerVan';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  Button,
  Badge,
  Icon,
  useToast,
  useColorModeValue,
  Textarea,
  Radio,
  RadioGroup,
  Stack,
  Stepper,
  Step,
  StepIndicator,
  StepStatus,
  StepIcon,
  StepNumber,
  StepTitle,
  StepSeparator,
  Input,
  FormControl,
  FormLabel,
  Checkbox,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  SliderMark,
  Progress,
  Divider,
  Center,
  Spinner,
  Image,
  Avatar
} from '@chakra-ui/react';
import { CheckIcon, WarningIcon, InfoIcon } from '@chakra-ui/icons';
import PageHeader from './shared/PageHeader';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import FuelGauge from './subComponents/FuelGauge';
import TemperatureVisualizer from './shared/TemperatureVisualizer';
import CategoryPhotoCapture from './shared/CategoryPhotoCapture';
import ContainerVanTypeahead from './ContainerVanTypeahead';
import { MIN_TEMP, MAX_TEMP } from '../constants/checklist';
import { computeFuelEstimate } from '../utils/containerVanChecklistUtils';

const ContainerVanPerformCheck: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user, selectedCompany, isSuperAdmin, hasFeature } = useAuth();
  
  // Search state
  const [vanSearchQuery, setVanSearchQuery] = useState('');
  const [selectedVan, setSelectedVan] = useState<ContainerVan | null>(null);
  
  // Checklist state
  const [checklistTemplate, setChecklistTemplate] = useState<ContainerVanChecklistTemplate | null>(null);
  const [currentCategories, setCurrentCategories] = useState<ContainerVanChecklistCategory[]>([]);
  const [currentView, setCurrentView] = useState<'checklist' | 'summary'>('checklist');
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [genSetRemainingHours, setGenSetRemainingHours] = useState<number>(0);
  
  // Photo state for each category
  const [categoryPhotos, setCategoryPhotos] = useState<Map<string, File>>(new Map());
  
  // Function to get fuel percentage from checklist data
  const getFuelPercentage = (): number => {
    for (const category of currentCategories) {
      for (const item of category.items) {
        if (item.text.toLowerCase().includes('fuel') && item.text.toLowerCase().includes('remaining')) {
          return item.value || 0;
        }
      }
    }
    return 0;
  };

  // Function to compute and update remaining hours
  const updateRemainingHours = () => {
    if (selectedVan?.genSetFullCapacity && selectedVan?.genSetAverageFuelConsumption) {
      const fuelPercentage = getFuelPercentage();
      const capacity = parseFloat(selectedVan.genSetFullCapacity) || 0;
      const consumption = parseFloat(selectedVan.genSetAverageFuelConsumption) || 0;
      
      if (capacity > 0 && consumption > 0) {
        const estimate = computeFuelEstimate(capacity, consumption, fuelPercentage);
        setGenSetRemainingHours(estimate.hoursRemaining);
      } else {
        setGenSetRemainingHours(0);
      }
    } else {
      setGenSetRemainingHours(0);
    }
  };
  
  // Photo handling functions - memoized to prevent unnecessary re-renders
  const handleCategoryPhotoCapture = useCallback((categoryId: string, file: File) => {
    setCategoryPhotos(prev => new Map(prev.set(categoryId, file)));
  }, []);

  const handleCategoryPhotoRemove = useCallback((categoryId: string) => {
    setCategoryPhotos(prev => {
      const newMap = new Map(prev);
      newMap.delete(categoryId);
      return newMap;
    });
  }, []);

  // Photo validation for required categories
  const validateCategoryPhotos = (): boolean => {
    for (const category of currentCategories) {
      if (category.isPhotoRequired && !categoryPhotos.has(category.id) && !category.photoUrl) {
        toast({
          title: 'Photo Required',
          description: `Please add a photo for ${category.name} category`,
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
        return false;
      }
    }
    return true;
  };

  // Validate current category photo specifically
  const validateCurrentCategoryPhoto = (): boolean => {
    const currentCategory = currentCategories[currentStep];
    if (currentCategory.isPhotoRequired && !categoryPhotos.has(currentCategory.id) && !currentCategory.photoUrl) {
      toast({
        title: 'Photo Required',
        description: `Please add a photo for ${currentCategory.name} category before proceeding`,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return false;
    }
    return true;
  };
  // Form state
  const [inspectionSummary, setInspectionSummary] = useState('');
  const [overallStatus, setOverallStatus] = useState<'green' | 'yellow' | 'red'>('green');
  const [signatureValid, setSignatureValid] = useState(false);
  const signatureRef = useRef<SignatureCanvas>(null);

  const cardBg = useColorModeValue('white', 'gray.800');
  
  // Calculate remaining hours whenever categories change or van is selected
  useEffect(() => {
    updateRemainingHours();
  }, [currentCategories, selectedVan]);
  
  const handleVanSelect = async (van: ContainerVan) => {
    setSelectedVan(van);
    setVanSearchQuery(van.vanNumber);
    await loadChecklistTemplate();
  };

  const handleVanSearchChange = (query: string) => {
    setVanSearchQuery(query);
    // Clear selected van if query doesn't match
    if (selectedVan && selectedVan.vanNumber !== query) {
      setSelectedVan(null);
    }
  };

  const loadChecklistTemplate = async () => {
    try {
      setIsLoadingTemplate(true);
      const template = await getContainerVanChecklistTemplate();
      
      if (template) {
        setChecklistTemplate(template);
        setCurrentCategories(JSON.parse(JSON.stringify(template.categories)));
      } else {
        toast({
          title: 'Template Not Found',
          description: 'Container van checklist template not found',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error loading template:', error);
      toast({
        title: 'Template Error',
        description: 'Failed to load checklist template',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingTemplate(false);
    }
  };

  const handleItemValueChange = (categoryIndex: number, itemIndex: number, value: any) => {
    setCurrentCategories(prev => {
      const updated = [...prev];
      updated[categoryIndex].items[itemIndex].value = value;
      
      // Check if this is a fuel-related item and recalculate remaining hours
      const item = updated[categoryIndex].items[itemIndex];
      if (item.text.toLowerCase().includes('fuel') && item.text.toLowerCase().includes('remaining')) {
        // Use setTimeout to ensure state is updated before recalculating
        setTimeout(() => {
          updateRemainingHours();
        }, 0);
      }
      
      return updated;
    });
  };

  const handleItemNotesChange = (categoryIndex: number, itemIndex: number, notes: string) => {
    setCurrentCategories(prev => {
      const updated = [...prev];
      updated[categoryIndex].items[itemIndex].notes = notes;
      return updated;
    });
  };

  // Validation function to check if current category has required fields filled
  const validateCurrentCategory = () => {
    const currentCategory = currentCategories[currentStep];
    const errors: string[] = [];

    currentCategory.items.forEach((item) => {
      if (item.required) {
        if (item.type === 'slider' || item.type === 'gauge') {
          if (!item.value || item.value === 0) {
            errors.push(`${item.text} is required and cannot be 0`);
          }
        } else if (item.type === 'text' || !item.type) {
          // Handle both explicit 'text' type and items without type (defaults to text input)
          if (!item.value || String(item.value).trim() === '') {
            errors.push(`${item.text} is required`);
          }
        } else if (item.type === 'checkbox') {
          if (!item.value) {
            errors.push(`${item.text} must be checked`);
          }
        }
      }
    });

    if (errors.length > 0) {
      toast({
        title: "Validation Error",
        description: errors.join(", "),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return false;
    }

    return true;
  };

  const handleNextCategory = () => {
    if (validateCurrentCategory() && validateCurrentCategoryPhoto()) {
      setCurrentStep(currentStep + 1);
    }
  };

  // Validation function for summary form
  const validateSummaryForm = () => {
    const errors: string[] = [];

    if (!inspectionSummary.trim()) {
      errors.push("Inspection Summary is required");
    }

    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      errors.push("Inspector Signature is required");
    }

    if (errors.length > 0) {
      toast({
        title: "Validation Error",
        description: errors.join(", "),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return false;
    }

    return true;
  };

  // Helper function to check if signature is valid
  const isSignatureValid = () => {
    return signatureRef.current && !signatureRef.current.isEmpty();
  };

  // Helper function to check if summary form is complete
  const isSummaryFormComplete = () => {
    return inspectionSummary.trim() && signatureValid;
  };

  // Handle signature changes
  const handleSignatureChange = () => {
    const isValid = isSignatureValid();
    setSignatureValid(!!isValid);
  };

  // Handle signature clear
  const handleSignatureClear = () => {
    signatureRef.current?.clear();
    setSignatureValid(false);
  };

  const renderFormField = (item: ContainerVanChecklistItem, categoryIndex: number, itemIndex: number) => {
    const value = item.value;

    switch (item.type) {
      case 'checkbox':
        return (
          <Checkbox
            isChecked={value === true}
            onChange={(e) => handleItemValueChange(categoryIndex, itemIndex, e.target.checked)}
            colorScheme="cyan"
          >
            {item.text}
          </Checkbox>
        );

      case 'slider':
        return (
          <VStack spacing={6} align="stretch">
            <Text fontWeight="medium" fontSize="md" color="gray.700">
              {item.text}
            </Text>
            
            {/* Temperature Visualizer - show if icon is "temp" */}
            {item.icon === 'temp' && (
              <Box display="flex" justifyContent="center" mb={4}>
                <TemperatureVisualizer
                  value={value || MIN_TEMP}
                  min={MIN_TEMP}
                  max={MIN_TEMP}
                  unit={item.unit || '°C'}
                  showValue={true}
                />
              </Box>
            )}
            
            <Box px={3} py={4}>
              <Slider
                value={value || (item.icon === 'temp' ? MIN_TEMP : 0)}
                onChange={(val) => handleItemValueChange(categoryIndex, itemIndex, val)}
                min={item.icon === 'temp' ? MIN_TEMP : 0}
                max={item.icon === 'temp' ? MAX_TEMP : 100}
                step={1}
                colorScheme="cyan"
              >
                {item.icon === 'temp' ? (
                  <>
                    <SliderMark value={MIN_TEMP} mt="6" ml="-4" fontSize="sm" color="gray.500">
                      {MIN_TEMP}
                    </SliderMark>
                    <SliderMark value={0} mt="6" ml="-2" fontSize="sm" color="gray.500">
                      0
                    </SliderMark>
                    <SliderMark value={MAX_TEMP} mt="6" ml="-3" fontSize="sm" color="gray.500">
                      {MAX_TEMP}
                    </SliderMark>
                  </>
                ) : (
                  <>
                    <SliderMark value={0} mt="6" ml="-2" fontSize="sm" color="gray.500">
                      0
                    </SliderMark>
                    <SliderMark value={50} mt="6" ml="-2" fontSize="sm" color="gray.500">
                      50
                    </SliderMark>
                    <SliderMark value={100} mt="6" ml="-4" fontSize="sm" color="gray.500">
                      100
                    </SliderMark>
                  </>
                )}
                <SliderMark
                  value={value || (item.icon === 'temp' ? MIN_TEMP : 0)}
                  textAlign="center"
                  bg="cyan.500"
                  color="white"
                  mt="-10"
                  ml="-5"
                  w="10"
                  fontSize="sm"
                  borderRadius="md"
                >
                  {value || (item.icon === 'temp' ? MIN_TEMP : 0)}
                </SliderMark>
                <SliderTrack>
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb />
              </Slider>
            </Box>
          </VStack>
        );

      case 'gauge':
        const getGaugeProps = (text: string) => {
          const lowerText = text.toLowerCase();
          if (lowerText.includes('fuel') || lowerText.includes('gas')) {
            return { colorScheme: 'fuel' as const, unit: '%', min: 0, max: 100 };
          } else if (lowerText.includes('temperature') || lowerText.includes('temp')) {
            return { colorScheme: 'temperature' as const, unit: '°C', min: MIN_TEMP, max: MAX_TEMP };
          } else if (lowerText.includes('pressure')) {
            return { colorScheme: 'pressure' as const, unit: 'PSI', min: 0, max: 200 };
          }
          return { colorScheme: 'fuel' as const, unit: '', min: 0, max: 100 };
        };

        const gaugeProps = getGaugeProps(item.text);
        
        return (
          <Box py={2}>
            <FuelGauge
              label={item.text}
              value={value || gaugeProps.min}
              onChange={(val) => handleItemValueChange(categoryIndex, itemIndex, val)}
              min={gaugeProps.min}
              max={gaugeProps.max}
              unit={gaugeProps.unit}
              colorScheme={gaugeProps.colorScheme}
            />
          </Box>
        );

      case 'textarea':
        return (
          <VStack spacing={2} align="stretch">
            <Text fontWeight="medium">{item.text}</Text>
            <Textarea
              value={value || ''}
              onChange={(e) => handleItemValueChange(categoryIndex, itemIndex, e.target.value)}
              placeholder="Enter details..."
              size="sm"
              rows={3}
            />
          </VStack>
        );

      default:
        return (
          <VStack spacing={2} align="stretch">
            <Text fontWeight="medium">{item.text}</Text>
            <Input
              value={value || ''}
              onChange={(e) => handleItemValueChange(categoryIndex, itemIndex, e.target.value)}
              placeholder="Enter value..."
              size="sm"
            />
          </VStack>
        );
    }
  };

  const calculateProgress = () => {
    const stats = calculateContainerVanChecklistStats(currentCategories);
    return stats.completionPercentage;
  };

  const handleGeneratePDF = async () => {
    if (!selectedVan || !selectedCompany) {
      toast({
        title: 'Cannot Export PDF',
        description: 'Please select a van before exporting.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const stats = calculateContainerVanChecklistStats(currentCategories);
      
      const pdfParams: ContainerVanPDFGenerationParams = {
        vanNumber: selectedVan.vanNumber,
        genSetNumber: selectedVan.genSetNumber,
        location: selectedVan.location,
        companyName: selectedCompany.companyName || 'Unknown Company',
        categories: currentCategories,
        totalRequired: stats.totalItems,
        completedRequired: stats.completedItems,
        isFullyComplete: stats.isComplete,
        overallStatus,
        inspectionSummary,
        signatureDataURL: signatureRef.current?.toDataURL(),
        inspectorName: user?.email || user?.uid || 'Unknown Inspector',
        checklistType: 'Container Van Inspection',
        genSetRemainingHours
      };

      const fileName = await generateContainerVanPDFSummary(pdfParams);
      
      toast({
        title: 'PDF Generated Successfully',
        description: `Checklist exported as ${fileName}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: 'PDF Generation Failed',
        description: 'There was an error generating the PDF. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSubmit = async () => {
    if (!selectedVan || !user || !selectedCompany) return;

    const stats = calculateContainerVanChecklistStats(currentCategories);
    
    if (!stats.isComplete) {
      toast({
        title: 'Incomplete Checklist',
        description: 'Please complete all required items before submitting',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validate required photos
    if (!validateCategoryPhotos()) {
      return;
    }

    try {
      setIsSubmitting(true);

      // Upload category photos
      const photoUploadData = Array.from(categoryPhotos.entries()).map(([categoryId, file]) => ({
        file,
        companyId: selectedCompany.companyId,
        categoryId,
        vanNumber: selectedVan.vanNumber
      }));

      let photoPaths: string[] = [];
      if (photoUploadData.length > 0) {
        toast({
          title: 'Uploading Photos',
          description: 'Please wait while photos are being uploaded...',
          status: 'info',
          duration: 2000,
          isClosable: true,
        });
        
        photoPaths = await uploadCategoryPhotos(photoUploadData);
      }

      // Update categories with photo storage paths
      const updatedCategories = currentCategories.map((category) => {
        const photoIndex = Array.from(categoryPhotos.keys()).indexOf(category.id);
        if (photoIndex !== -1) {
          return {
            ...category,
            photoUrl: photoPaths[photoIndex]
          };
        }
        return category;
      });

      const checklistData: Omit<ContainerVanChecklistData, 'id'> = {
        vanNumber: selectedVan.vanNumber,
        genSetNumber: selectedVan.genSetNumber,
        vanId: selectedVan.id,
        location: selectedVan.location,
        companyId: selectedCompany.companyId,
        status: 'completed',
        overallStatus: convertColorStatusToDescriptive(overallStatus),
        categories: updatedCategories,
        inspectionSummary,
        signatureDataURL: signatureRef.current?.toDataURL(),
        genSetRemainingHours,
        created_at: Timestamp.now(),
        created_by: user.email || user.uid,
        completed_at: Timestamp.now(),
        completed_by: user.email || user.uid,
        isComplete: true
      };

      await saveContainerVanChecklist(checklistData);

      toast({
        title: 'Checklist Submitted',
        description: 'Container van checklist has been saved successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      navigate('/container-van-checklist');
    } catch (error) {
      console.error('Error submitting checklist:', error);
      toast({
        title: 'Submission Error',
        description: 'Failed to save checklist',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentCategory = currentCategories[currentStep];
  const progress = calculateProgress();

  if (!user || !selectedCompany) {
    return (
      <Container maxW="md" py={8}>
        <VStack spacing={4}>
          <Text>Please log in and select a company to continue.</Text>
          <Button onClick={() => navigate('/container-van-checklist')} colorScheme="cyan">
            Back to Container Van Checklist
          </Button>
        </VStack>
      </Container>
    );
  }

  return (
    <Box minH="100vh" bgGradient="linear(135deg, blue.50, purple.50, pink.50)" w="100%" overflowX="hidden">
      <PageHeader
        title="Perform Check"
        subtitle="Container Van Inspection"
        icon="📝"
        onBack={() => navigate('/container-van-checklist')}
      />

      <Container maxW="6xl" py={12} px={4}>
        {/* Checklist View */}
        {currentView === 'checklist' && (
          <VStack spacing={6} w="100%">
          {/* Van Search Section */}
          {!selectedVan && (
            <Card
              w="100%"
              bg="rgba(255, 255, 255, 0.8)"
              backdropFilter="blur(20px)"
              borderRadius="2xl"
              boxShadow="0 8px 32px rgba(0,0,0,0.12)"
              border="1px solid"
              borderColor="whiteAlpha.500"
            >
              <CardBody>
                <VStack spacing={4}>
                  <VStack spacing={2} textAlign="center">
                    <Text fontSize="lg" fontWeight="bold" color="cyan.600">
                      Search Container Van
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Enter van number to begin inspection
                    </Text>
                  </VStack>

                  {(hasFeature("container_van_checklist.perform", selectedCompany?.companyId) || isSuperAdmin) &&
                  <VStack spacing={3} w="100%">
                    <ContainerVanTypeahead
                      placeholder="Search and select van number..."
                      value={vanSearchQuery}
                      onChange={handleVanSearchChange}
                      onSelect={handleVanSelect}
                      companyId={selectedCompany?.companyId}
                      size="lg"
                    />
                    
                    {selectedVan && (() => {
                      const van = selectedVan as ContainerVan; // Explicit type assertion
                      return (
                        <Card w="100%" bg="green.50" borderColor="green.200" variant="outline">
                          <CardBody py={3}>
                            <VStack spacing={2} align="start">
                              <HStack>
                                <CheckIcon color="green.500" boxSize={4} />
                                <Text fontWeight="medium" fontSize="sm">Van Selected</Text>
                              </HStack>
                              <Text fontSize="xs" color="gray.600">
                                Van: {van.vanNumber} | GenSet: {van.genSetNumber}
                              </Text>
                              <Text fontSize="xs" color="gray.600">
                                Location: {van.location}
                              </Text>
                            </VStack>
                          </CardBody>
                        </Card>
                      );
                    })()}
                  </VStack>
                  }
                </VStack>
              </CardBody>
            </Card>
          )}

          {/* Loading Template */}
          {selectedVan && isLoadingTemplate && (
            <Card w="100%" bg={cardBg}>
              <CardBody>
                <VStack spacing={4} py={8}>
                  <Spinner size="lg" color="cyan.500" />
                  <Text color="gray.600">Loading checklist template...</Text>
                </VStack>
              </CardBody>
            </Card>
          )}

          {/* Checklist Form */}
          {selectedVan && checklistTemplate && currentCategories.length > 0 && (
            <>
              {/* Selected Van Info */}
              <Card w="100%" bg="cyan.50" border="1px solid" borderColor="cyan.200">
                <CardBody>
                  <HStack spacing={4} align="center">
                    {/* Container Van Photo */}
                    {selectedVan.photo ? (
                      <Image
                        src={selectedVan.photo}
                        alt={`Container van ${selectedVan.vanNumber}`}
                        boxSize="70px"
                        objectFit="cover"
                        borderRadius="md"
                        border="2px solid"
                        borderColor="cyan.300"
                        flexShrink={0}
                      />
                    ) : (
                      <Avatar
                        name={selectedVan.vanNumber}
                        boxSize="70px"
                        bg="cyan.200"
                        color="cyan.700"
                        fontSize="md"
                        flexShrink={0}
                      />
                    )}
                    
                    {/* Van Details */}
                    <VStack spacing={2} align="start" flex={1}>
                      <Text fontSize="lg" fontWeight="bold" color="cyan.700">
                        {selectedVan.vanNumber}
                      </Text>
                      <VStack spacing={1} align="start" w="100%">
                        <Text fontSize="sm" color="cyan.600">
                          <strong>GenSet:</strong> {selectedVan.genSetNumber}
                        </Text>
                        <Text fontSize="sm" color="cyan.600">
                          <strong>Location:</strong> {selectedVan.location}
                        </Text>
                      </VStack>
                    </VStack>
                  </HStack>
                </CardBody>
              </Card>

              {/* Progress */}
              <Box w="100%">
                <VStack spacing={2}>
                  <HStack justify="space-between" w="100%">
                    <Text fontSize="sm" fontWeight="medium">Progress</Text>
                    <Text fontSize="sm" color="cyan.600">{progress}%</Text>
                  </HStack>
                  <Progress value={progress} colorScheme="cyan" w="100%" borderRadius="full" />
                </VStack>
              </Box>

              {/* Category Stepper */}
              <Box w="100%">
                <Stepper index={currentStep} orientation="horizontal" size="sm">
                  {currentCategories.map((category) => (
                    <Step key={category.id}>
                      <StepIndicator>
                        <StepStatus
                          complete={<StepIcon />}
                          incomplete={<StepNumber />}
                          active={<StepNumber />}
                        />
                      </StepIndicator>
                      <Box flexShrink="0">
                        <StepTitle fontSize="xs">{category.step_label}</StepTitle>
                      </Box>
                      <StepSeparator />
                    </Step>
                  ))}
                </Stepper>
              </Box>

              {/* Current Category */}
              {currentCategory && (
                <Card w="100%" bg={cardBg} boxShadow="md">
                  <CardBody>
                    <VStack spacing={6} align="stretch">
                      <Box textAlign="center">
                        <Badge
                          colorScheme="cyan"
                          fontSize="sm"
                          px={3}
                          py={1}
                          borderRadius="full"
                          mb={2}
                        >
                          Category {currentStep + 1} of {currentCategories.length}
                        </Badge>
                        <Text fontSize="xl" fontWeight="bold" color={currentCategory.color}>
                          {currentCategory.name}
                        </Text>
                      </Box>

                      <Divider />

                      {/* Category Items */}
                      <VStack spacing={6} align="stretch">
                        {currentCategory.items.map((item, itemIndex) => (
                          <Box key={item.id}>
                            <VStack spacing={item.type === 'slider' || item.type === 'gauge' ? 8 : 3} align="stretch">
                              {renderFormField(item, currentStep, itemIndex)}
                              
                              {/* Notes field for each item */}
                              <FormControl mt={item.type === 'slider' || item.type === 'gauge' ? 4 : 0}>
                                <FormLabel fontSize="sm" color="gray.600">
                                  Notes (optional)
                                </FormLabel>
                                <Textarea
                                  value={item.notes || ''}
                                  onChange={(e) => handleItemNotesChange(currentStep, itemIndex, e.target.value)}
                                  placeholder="Add any notes or observations..."
                                  size="sm"
                                  rows={2}
                                />
                              </FormControl>
                            </VStack>
                            {itemIndex < currentCategory.items.length - 1 && <Divider mt={4} />}
                          </Box>
                        ))}
                      </VStack>

                      {/* Category Photo Capture */}
                      <Divider />
                      <CategoryPhotoCapture
                        key={`photo-${currentCategory.id}`}
                        label={`Photo Evidence for ${currentCategory.name}`}
                        isRequired={currentCategory.isPhotoRequired}
                        existingPhotoUrl={currentCategory.photoUrl}
                        onPhotoCapture={(file) => handleCategoryPhotoCapture(currentCategory.id, file)}
                        onPhotoRemove={() => handleCategoryPhotoRemove(currentCategory.id)}
                        currentPhotoFile={categoryPhotos.get(currentCategory.id)}
                      />

                      {/* Navigation Buttons */}
                      <HStack justify="space-between" pt={4}>
                        <Button
                          variant="outline"
                          colorScheme="gray"
                          onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                          isDisabled={currentStep === 0}
                        >
                          Previous
                        </Button>
                        
                        {currentStep < currentCategories.length - 1 ? (
                          <Button
                            colorScheme="cyan"
                            onClick={handleNextCategory}
                          >
                            Next
                          </Button>
                        ) : (
                          <Button
                            colorScheme="green"
                            onClick={() => {
                              if (validateCurrentCategory() && validateCurrentCategoryPhoto()) {
                                setCurrentView('summary');
                              }
                            }}
                            leftIcon={<CheckIcon />}
                          >
                            Complete
                          </Button>
                        )}
                      </HStack>
                    </VStack>
                  </CardBody>
                </Card>
              )}
            </>
          )}
          </VStack>
        )}

        {/* Summary View */}
        {currentView === 'summary' && selectedVan && checklistTemplate && (
          <VStack spacing={6} w="100%">
            {/* Header */}
            <Card w="100%" bg="green.50" borderColor="green.200" boxShadow="md">
              <CardBody>
                <VStack align="start" spacing={2}>
                  <Text fontSize="xl" fontWeight="bold" color="green.600">
                    Inspection Summary
                  </Text>
                  <Text color="gray.600">
                    Complete the inspection details below
                  </Text>
                  <HStack>
                    <Text fontSize="sm" color="gray.600">Van:</Text>
                    <Text fontWeight="medium">{selectedVan.vanNumber}</Text>
                  </HStack>
                  <HStack>
                    <Text fontSize="sm" color="gray.600">GenSet Remaining Operating Hours</Text>
                    <Text fontWeight="medium">
                      {genSetRemainingHours > 0 
                        ? `${genSetRemainingHours.toFixed(1)} hours`
                        : 'N/A'
                      }
                    </Text>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>

            {/* Summary Form */}
            <Card w="100%" bg={cardBg} boxShadow="md" key="summary-form">
              <CardBody>
                <VStack spacing={6} align="stretch">
                  {/* Overall Status */}
                  <FormControl key="overall-status">
                    <FormLabel>Overall Status</FormLabel>
                    <RadioGroup value={overallStatus} onChange={(value: 'green' | 'yellow' | 'red') => setOverallStatus(value)}>
                      <Stack direction="row" spacing={6}>
                        <Radio value="green" colorScheme="green">
                          <HStack>
                            <CheckIcon color="green.500" />
                            <Text>Pass</Text>
                          </HStack>
                        </Radio>
                        <Radio value="yellow" colorScheme="yellow">
                          <HStack>
                            <WarningIcon color="yellow.500" />
                            <Text>Warning</Text>
                          </HStack>
                        </Radio>
                        <Radio value="red" colorScheme="red">
                          <HStack>
                            <InfoIcon color="red.500" />
                            <Text>Fail</Text>
                          </HStack>
                        </Radio>
                      </Stack>
                    </RadioGroup>
                  </FormControl>

                  {/* GenSet Remaining Operating Hours */}
                  <FormControl key="genset-hours">
                    <FormLabel>GenSet Remaining Operating Hours</FormLabel>
                    <Box
                      p={3}
                      bg="gray.50"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="gray.200"
                    >
                      <Text fontSize="lg" fontWeight="bold" color="blue.600">
                        {genSetRemainingHours > 0 
                          ? `${genSetRemainingHours.toFixed(1)} hours`
                          : 'Unable to calculate (check fuel percentage and GenSet data)'
                        }
                      </Text>
                      <Text fontSize="sm" color="gray.600" mt={1}>
                        Based on fuel percentage, capacity ({selectedVan.genSetFullCapacity}L), 
                        and consumption rate ({selectedVan.genSetAverageFuelConsumption}L/hr)
                      </Text>
                    </Box>
                  </FormControl>

                  {/* Inspection Summary */}
                  <FormControl isRequired key="inspection-summary">
                    <FormLabel>Inspection Summary *</FormLabel>
                    <Textarea
                      value={inspectionSummary}
                      onChange={(e) => setInspectionSummary(e.target.value)}
                      placeholder="Add summary of the inspection..."
                      rows={4}
                      isInvalid={!inspectionSummary.trim()}
                    />
                  </FormControl>

                  {/* Signature */}
                  <FormControl isRequired key="signature">
                    <FormLabel>Inspector Signature *</FormLabel>
                    <Box border="2px dashed" borderColor="gray.300" borderRadius="md" p={4}>
                      <SignatureCanvas
                        ref={signatureRef}
                        onEnd={handleSignatureChange}
                        canvasProps={{
                          width: 300,
                          height: 150,
                          className: 'signature-canvas',
                          style: { width: '100%', height: '150px' }
                        }}
                      />
                      <Center mt={2}>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleSignatureClear}
                        >
                          Clear Signature
                        </Button>
                      </Center>
                    </Box>
                  </FormControl>
                </VStack>
              </CardBody>
            </Card>

            {/* Export PDF Button */}
            <Card w="100%" bg={cardBg} boxShadow="md">
              <CardBody>
                <Button
                  w="100%"
                  colorScheme="blue"
                  variant="outline"
                  onClick={() => {
                    if (validateSummaryForm()) {
                      handleGeneratePDF();
                    }
                  }}
                  leftIcon={<Icon as={InfoIcon} />}
                  isDisabled={!isSummaryFormComplete()}
                >
                  Export PDF
                </Button>
              </CardBody>
            </Card>

            {/* Action Buttons */}
            <Card w="100%" bg={cardBg} boxShadow="md">
              <CardBody>
                <HStack spacing={3} justify="center">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentView('checklist')}
                  >
                    Back
                  </Button>
                  <Button
                    colorScheme="green"
                    onClick={() => {
                      if (validateSummaryForm()) {
                        handleSubmit();
                      }
                    }}
                    isLoading={isSubmitting}
                    loadingText="Submitting..."
                    isDisabled={!isSummaryFormComplete()}
                  >
                    Submit Inspection
                  </Button>
                </HStack>
              </CardBody>
            </Card>
          </VStack>
        )}
      </Container>

    </Box>
  );
};

export default ContainerVanPerformCheck;